import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Upload, Download, Copy, Check, ChevronDown, ChevronUp, Sun, Moon, Monitor, Unlink } from 'lucide-react';
import { useAuthStore, useFeedsStore } from '../store/useStore';
import { auth as authApi, feeds as feedsApi, mastodon as mastodonApi } from '../lib/api';
import { useTheme } from '../hooks/useTheme';
import toast from 'react-hot-toast';
import DigestPreferences from './DigestPreferences';
import InviteCodeManager from './InviteCodeManager';

type TabId = 'account' | 'appearance' | 'sharing' | 'preferences';

const tabs: { id: TabId; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'sharing', label: 'Sharing & tools' },
  { id: 'preferences', label: 'Preferences' },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-edge py-6">
      <div className="kicker text-ink mb-4">{title}</div>
      {children}
    </section>
  );
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);
  const setFeeds = useFeedsStore((state) => state.setFeeds);
  const { theme, setTheme } = useTheme();

  const [displayName, setDisplayName] = useState(user?.displayName || '');

  const [mastodonStatus, setMastodonStatus] = useState<{
    connected: boolean;
    instance?: string;
    username?: string;
    autoPost?: boolean;
  }>({ connected: false });
  const [mastodonLoading, setMastodonLoading] = useState(true);
  const [mastodonConnecting, setMastodonConnecting] = useState(false);
  const [mastodonDisconnecting, setMastodonDisconnecting] = useState(false);
  const [mastodonTogglingAutoPost, setMastodonTogglingAutoPost] = useState(false);
  const [bio, setBio] = useState(user?.bio || '');
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [importingOpml, setImportingOpml] = useState(false);
  const [exportingOpml, setExportingOpml] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [bookmarkletCopied, setBookmarkletCopied] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<TabId>('account');

  useEffect(() => {
    async function fetchMastodonStatus() {
      try {
        const status = await mastodonApi.getStatus(token);
        setMastodonStatus(status);
      } catch (error) {
        console.error('Failed to fetch Mastodon status:', error);
      } finally {
        setMastodonLoading(false);
      }
    }

    fetchMastodonStatus();

    const mastodonParam = searchParams.get('mastodon');
    if (mastodonParam === 'success') {
      toast.success('Mastodon connected');
      fetchMastodonStatus();
      setActiveTab('sharing');
      window.history.replaceState({}, '', '/settings');
    } else if (mastodonParam === 'error') {
      const message = searchParams.get('message') || 'Failed to connect Mastodon account';
      toast.error(message);
      window.history.replaceState({}, '', '/settings');
    }
  }, [token, searchParams]);

  async function handleMastodonConnect() {
    const instance = prompt('Your Mastodon instance:', 'social.fourfold.co');
    if (!instance) return;

    setMastodonConnecting(true);
    try {
      const { authUrl } = await mastodonApi.connect(token, instance);
      window.location.href = authUrl;
    } catch (error: any) {
      toast.error(error.message || 'Failed to connect to Mastodon');
      setMastodonConnecting(false);
    }
  }

  async function handleMastodonDisconnect() {
    if (!confirm('Disconnect your Mastodon account?')) return;

    setMastodonDisconnecting(true);
    try {
      await mastodonApi.disconnect(token);
      setMastodonStatus({ connected: false });
      toast.success('Mastodon disconnected');
    } catch (error: any) {
      toast.error(error.message || 'Failed to disconnect Mastodon');
    } finally {
      setMastodonDisconnecting(false);
    }
  }

  async function handleMastodonAutoPostToggle() {
    setMastodonTogglingAutoPost(true);
    try {
      const newValue = !mastodonStatus.autoPost;
      await mastodonApi.updateSettings(token, newValue);
      setMastodonStatus({ ...mastodonStatus, autoPost: newValue });
      toast.success(newValue ? 'Auto-posting on' : 'Auto-posting off');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update settings');
    } finally {
      setMastodonTogglingAutoPost(false);
    }
  }

  async function handleDefaultPublicToggle() {
    try {
      const newValue = !user?.defaultPublicShares;
      await authApi.updateProfile(token, { defaultPublicShares: newValue });
      if (user) {
        updateUser({ ...user, defaultPublicShares: newValue });
      }
      toast.success(newValue ? 'Shares are public by default' : 'Shares are private by default');
    } catch {
      toast.error('Failed to update setting');
    }
  }

  function handleCopyToken() {
    navigator.clipboard.writeText(token);
    setTokenCopied(true);
    toast.success('Token copied');
    setTimeout(() => setTokenCopied(false), 2000);
  }

  // Bookmarklet with the auth token embedded; loads the real script from this server
  function getBookmarkletCode() {
    const serverUrl = window.location.origin;

    const bookmarkletJS = `
(function(){
if(window.__OURSS_TOKEN__){return;}
window.__OURSS_TOKEN__='${token}';
var s=document.createElement('script');
s.src='${serverUrl}/bookmarklet.js?t='+Date.now();
document.body.appendChild(s);
})();void(0);
    `.trim();

    const minified = bookmarkletJS
      .replace(/\n/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\s*([{};,()=])\s*/g, '$1');

    return `javascript:${encodeURIComponent(minified)}`;
  }

  function handleCopyBookmarklet() {
    navigator.clipboard.writeText(getBookmarkletCode());
    setBookmarkletCopied(true);
    toast.success('Bookmarklet copied');
    setTimeout(() => setBookmarkletCopied(false), 2000);
  }

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault();

    setUpdatingProfile(true);

    try {
      const updatedUser = await authApi.updateProfile(token, {
        displayName: displayName.trim() || undefined,
        bio: bio.trim() || undefined,
      });

      updateUser(updatedUser);
      toast.success('Profile saved');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setUpdatingProfile(false);
    }
  }

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Use a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploadingAvatar(true);

    try {
      const updatedUser = await authApi.uploadAvatar(token, file);
      updateUser(updatedUser);
      toast.success('Photo uploaded');

      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setUpdatingPassword(true);

    try {
      await authApi.updatePassword(token, { currentPassword, newPassword });

      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      if (error.message.includes('Current password is incorrect')) {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setUpdatingPassword(false);
    }
  }

  async function handleOpmlImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.opml') && !file.name.endsWith('.xml')) {
      toast.error('Pick an OPML file (.opml or .xml)');
      return;
    }

    setImportingOpml(true);

    try {
      const result = await feedsApi.importOpml(token, file);

      toast.success(
        `Imported ${result.summary.added}, skipped ${result.summary.skipped} already subscribed${result.summary.errors > 0 ? `, ${result.summary.errors} failed` : ''}`
      );

      const feedsData = await feedsApi.getAll(token);
      setFeeds(feedsData);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to import OPML file');
    } finally {
      setImportingOpml(false);
    }
  }

  async function handleOpmlExport() {
    setExportingOpml(true);

    try {
      await feedsApi.exportOpml(token);
      toast.success('OPML downloaded');
    } catch {
      toast.error('Failed to export OPML file');
    } finally {
      setExportingOpml(false);
    }
  }

  const passwordField = (
    id: string,
    label: string,
    value: string,
    setValue: (v: string) => void,
    shown: boolean,
    setShown: (v: boolean) => void,
    placeholder: string,
    minLength?: number,
  ) => (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      <div className="relative">
        <input
          type={shown ? 'text' : 'password'}
          id={id}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          minLength={minLength}
          className="field pr-10"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setShown(!shown)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
          aria-label={shown ? 'Hide password' : 'Show password'}
        >
          {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const themeOptions: Array<{ id: 'light' | 'dark' | 'system'; label: string; icon: React.ElementType }> = [
    { id: 'light', label: 'Light', icon: Sun },
    { id: 'dark', label: 'Dark', icon: Moon },
    { id: 'system', label: 'System', icon: Monitor },
  ];

  const shortcuts = [
    ['j / k', 'Next / previous article'],
    ['Enter', 'Open the selected article'],
    ['Esc', 'Back to the list'],
    ['l', 'Like'],
    ['s', 'Share'],
    ['m', 'Mark as read'],
    ['⇧ A', 'Mark everything in view as read'],
    ['/', 'Search and commands'],
    ['?', 'All shortcuts'],
  ];

  return (
    <div className="bg-canvas min-h-full pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-stretch border-b border-edge">
        <button onClick={() => navigate('/read')} className="tool" title="Back to Read">
          <ArrowLeft className="w-3.5 h-3.5" /> Read
        </button>
        <div className="flex items-center pl-4 lg:pl-6">
          <h1 className="text-lg">Settings</h1>
        </div>
      </div>

      {/* Tab rail */}
      <div className="rail sticky top-0 z-10 bg-canvas">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`rail-tab ${activeTab === tab.id ? 'is-active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="pl-4 pr-4 lg:pl-12 lg:pr-8 max-w-[44rem]">
        {/* Account */}
        {activeTab === 'account' && (
          <>
            <Section title="Identity">
              <dl className="grid grid-cols-[7rem_1fr] gap-y-2 text-[15px] mb-6">
                <dt className="kicker pt-0.5">Username</dt>
                <dd className="text-ink font-mono text-sm">{user?.username}</dd>
                <dt className="kicker pt-0.5">Email</dt>
                <dd className="text-ink font-mono text-sm">{user?.email}</dd>
              </dl>

              <div className="flex items-start gap-4 mb-6">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt="Avatar"
                    className="w-16 h-16 rounded-full object-cover avatar-duotone flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-ink text-canvas flex items-center justify-center flex-shrink-0 font-mono text-lg">
                    {user?.displayName?.[0] || user?.username[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload"
                    disabled={uploadingAvatar}
                  />
                  <label
                    htmlFor="avatar-upload"
                    className={`btn cursor-pointer ${uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingAvatar ? 'Uploading…' : 'Upload photo'}
                  </label>
                  <p className="kicker mt-2">JPEG, PNG, or WebP · max 5MB</p>
                </div>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label htmlFor="displayName" className="label">Display name</label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={50}
                    placeholder="Optional. Falls back to your username."
                    className="field"
                  />
                </div>

                <div>
                  <label htmlFor="bio" className="label">Bio</label>
                  <textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    maxLength={500}
                    rows={4}
                    placeholder="A line or two about what you read."
                    className="field resize-none"
                  />
                  <p className="stamp mt-1">{bio.length} / 500</p>
                </div>

                <button type="submit" disabled={updatingProfile} className="btn btn-accent">
                  {updatingProfile ? 'Saving…' : 'Save profile'}
                </button>
              </form>
            </Section>

            <Section title="Password">
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="flex items-center justify-between w-full text-left text-[15px] text-ink"
                aria-expanded={showPasswordSection}
              >
                <span>Change password</span>
                {showPasswordSection ? <ChevronUp className="w-4 h-4 text-ink-muted" /> : <ChevronDown className="w-4 h-4 text-ink-muted" />}
              </button>

              {showPasswordSection && (
                <form onSubmit={handlePasswordSubmit} className="space-y-4 mt-4">
                  {passwordField('currentPassword', 'Current password', currentPassword, setCurrentPassword, showCurrentPassword, setShowCurrentPassword, 'Current password')}
                  {passwordField('newPassword', 'New password', newPassword, setNewPassword, showNewPassword, setShowNewPassword, 'At least 8 characters', 8)}
                  {passwordField('confirmPassword', 'Confirm new password', confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword, 'Repeat the new password')}
                  <button type="submit" disabled={updatingPassword} className="btn btn-ink">
                    {updatingPassword ? 'Updating…' : 'Update password'}
                  </button>
                </form>
              )}
            </Section>
          </>
        )}

        {/* Appearance */}
        {activeTab === 'appearance' && (
          <Section title="Mode">
            <div className="inline-flex border border-edge">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const active = theme === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => setTheme(option.id)}
                    className={`flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider border-r border-edge last:border-r-0 ${
                      active ? 'bg-ink text-canvas' : 'text-ink-secondary hover:bg-surface-sunken'
                    }`}
                    aria-pressed={active}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="text-sm text-ink-muted mt-3">
              Light and dark share the same ink, paper, and one accent. System follows your device.
            </p>
          </Section>
        )}

        {/* Sharing & tools */}
        {activeTab === 'sharing' && (
          <>
            <Section title="Sharing">
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <span>
                  <span className="block text-[15px] text-ink">Public by default</span>
                  <span className="block text-sm text-ink-muted mt-0.5">
                    New shares also go to the instance's public feed unless you uncheck it.
                  </span>
                </span>
                <button
                  type="button"
                  onClick={handleDefaultPublicToggle}
                  className={`switch ${user?.defaultPublicShares ? 'is-on' : ''}`}
                  role="switch"
                  aria-checked={!!user?.defaultPublicShares}
                />
              </label>

              <div className="mt-6 pt-4 border-t border-edge-subtle">
                <div className="label">Your public page</div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/u/${user?.username}`}
                    className="field field-mono"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/u/${user?.username}`);
                      toast.success('Link copied');
                    }}
                    className="btn"
                  >
                    Copy
                  </button>
                  <a
                    href={`/u/${user?.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                  >
                    Open
                  </a>
                </div>
                <p className="text-sm text-ink-muted mt-2">
                  Your public shares and their RSS feed live here.
                </p>
              </div>
            </Section>

            <Section title="Mastodon">
              <p className="text-sm text-ink-secondary mb-4">
                Connect an account and public shares can post to your timeline.
              </p>

              {mastodonLoading ? (
                <span className="kicker">Loading…</span>
              ) : mastodonStatus.connected ? (
                <div className="border border-edge">
                  <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-edge-subtle">
                    <div>
                      <div className="font-mono text-sm text-ink">@{mastodonStatus.username}@{mastodonStatus.instance}</div>
                      <div className="kicker text-pine">Connected</div>
                    </div>
                    <button
                      onClick={handleMastodonDisconnect}
                      disabled={mastodonDisconnecting}
                      className="btn btn-quiet"
                    >
                      <Unlink className="w-3.5 h-3.5" />
                      {mastodonDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </div>
                  <label className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer">
                    <span>
                      <span className="block text-[15px] text-ink">Auto-post public shares</span>
                      <span className="block text-sm text-ink-muted mt-0.5">Post to Mastodon when a share is public</span>
                    </span>
                    <button
                      type="button"
                      onClick={handleMastodonAutoPostToggle}
                      disabled={mastodonTogglingAutoPost}
                      className={`switch ${mastodonStatus.autoPost ? 'is-on' : ''} ${mastodonTogglingAutoPost ? 'opacity-50' : ''}`}
                      role="switch"
                      aria-checked={!!mastodonStatus.autoPost}
                    />
                  </label>
                </div>
              ) : (
                <button onClick={handleMastodonConnect} disabled={mastodonConnecting} className="btn btn-ink">
                  {mastodonConnecting ? 'Connecting…' : 'Connect Mastodon'}
                </button>
              )}
            </Section>

            <Section title="Feeds · OPML">
              <p className="text-sm text-ink-secondary mb-4">
                Move your subscriptions in or out. Folders come along.
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".opml,.xml"
                  onChange={handleOpmlImport}
                  className="hidden"
                  id="opml-import"
                  disabled={importingOpml}
                />
                <label
                  htmlFor="opml-import"
                  className={`btn cursor-pointer ${importingOpml ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  {importingOpml ? 'Importing…' : 'Import OPML'}
                </label>
                <button onClick={handleOpmlExport} disabled={exportingOpml} className="btn btn-ink">
                  <Download className="w-3.5 h-3.5" />
                  {exportingOpml ? 'Exporting…' : 'Export OPML'}
                </button>
              </div>
            </Section>

            <Section title="Browser extension">
              <p className="text-sm text-ink-secondary mb-4">
                Subscribe to a site's feed with one click from Chrome.
              </p>
              <div className="label">Auth token</div>
              <div className="flex gap-2 mb-2">
                <input type="text" value={token} readOnly className="field field-mono" />
                <button onClick={handleCopyToken} className="btn">
                  {tokenCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {tokenCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <ol className="text-sm text-ink-secondary space-y-1 mb-4 list-decimal pl-5">
                <li>Download the extension (ZIP) and unzip it.</li>
                <li>Open <code className="font-mono text-xs">chrome://extensions/</code> and turn on Developer mode.</li>
                <li>Load unpacked, pick the folder.</li>
                <li>Open the extension and paste the token above.</li>
              </ol>
              <a href="/browser-extension.zip" download className="btn">
                <Download className="w-3.5 h-3.5" />
                Download extension
              </a>
            </Section>

            <Section title="Bookmarklet">
              <p className="text-sm text-ink-secondary mb-4">
                Works in any browser, desktop or phone. Drag the button to your bookmarks bar; on a phone, copy the code into a bookmark's URL.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <a
                  href={getBookmarkletCode()}
                  className="btn btn-ink cursor-move"
                  onClick={(e) => e.preventDefault()}
                >
                  Subscribe in ourss
                </a>
                <span className="kicker">← drag to bookmarks bar</span>
              </div>
              <button onClick={handleCopyBookmarklet} className="btn">
                {bookmarkletCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {bookmarkletCopied ? 'Copied' : 'Copy bookmarklet code'}
              </button>
              <p className="text-sm text-ink-muted mt-3">
                On mobile Chrome, run it by typing the bookmark's name in the address bar. Tapping it from the bookmarks menu doesn't work.
              </p>
            </Section>
          </>
        )}

        {/* Preferences */}
        {activeTab === 'preferences' && (
          <>
            <Section title="Invites">
              <InviteCodeManager />
            </Section>

            <Section title="Digest">
              <DigestPreferences />
            </Section>

            <Section title="Keyboard">
              <button
                onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
                className="flex items-center justify-between w-full text-left text-[15px] text-ink"
                aria-expanded={showKeyboardShortcuts}
              >
                <span>Shortcuts</span>
                {showKeyboardShortcuts ? <ChevronUp className="w-4 h-4 text-ink-muted" /> : <ChevronDown className="w-4 h-4 text-ink-muted" />}
              </button>
              {showKeyboardShortcuts && (
                <div className="mt-3 border-t border-edge-subtle">
                  {shortcuts.map(([keys, description]) => (
                    <div key={keys} className="shortcut-row">
                      <span className="shortcut-description">{description}</span>
                      <kbd>{keys}</kbd>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
